

























































































































































import { Component, Vue } from 'vue-property-decorator';
import {
  PopupNoticeItem,
  PopupNoticeQuestion,
  popupNoticeAckApi,
  popupNoticeCloseApi,
  popupNoticeSubmitFeedbackApi
} from '@/api/popupNotice';

@Component({
  name: 'PopupNoticeDialog'
})
export default class PopupNoticeDialog extends Vue {
  public isShow = false;
  public loading = false;
  public noticeList: PopupNoticeItem[] = [];
  public currentIndex = 0;
  public form: any = {
    delayToTime: '',
    feedbackText: '',
    answerMap: {}
  };

  get currentNotice(): PopupNoticeItem | null {
    return this.noticeList[this.currentIndex] || null;
  }

  get questionList(): PopupNoticeQuestion[] {
    return this.currentNotice?.questionList || [];
  }

  get hasMultipleNotice(): boolean {
    return this.noticeList.length > 1;
  }

  get hasInteractiveSection(): boolean {
    const notice = this.currentNotice;
    return !!(
      notice &&
      (
        this.questionList.length ||
        notice.isAllowDelay === 1 ||
        notice.isAllowFeedback === 1
      )
    );
  }

  get safePopupContent(): string {
    return this.formatPopupNoticeContent(this.currentNotice?.popupContent || '');
  }

  get canClose(): boolean {
    const notice = this.currentNotice;
    if (!notice) return true;
    return notice.isForceShow !== 1 || notice.isAllowClose === 1;
  }

  get shouldShowAck(): boolean {
    const notice = this.currentNotice;
    if (!notice) return false;
    return notice.isNeedAck === 1 || (!this.canClose && notice.isAllowFeedback !== 1 && !this.questionList.length);
  }

  public open(list: PopupNoticeItem[]) {
    this.noticeList = Array.isArray(list) ? list.filter(item => item && item.id) : [];
    this.currentIndex = 0;
    this.resetForm();
    this.isShow = this.noticeList.length > 0;
  }

  public resetForm() {
    this.form.delayToTime = '';
    this.form.feedbackText = '';
    this.form.answerMap = {};
  }

  public changeCurrent(index: number) {
    if (index < 0 || index >= this.noticeList.length) return;
    this.currentIndex = index;
    this.resetForm();
  }

  public goPrev() {
    if (!this.hasMultipleNotice) return;
    this.changeCurrent(this.currentIndex <= 0 ? this.noticeList.length - 1 : this.currentIndex - 1);
  }

  public goNext() {
    if (!this.hasMultipleNotice) return;
    this.changeCurrent(this.currentIndex >= this.noticeList.length - 1 ? 0 : this.currentIndex + 1);
  }

  public finishCurrent() {
    this.noticeList.splice(this.currentIndex, 1);
    if (!this.noticeList.length) {
      this.isShow = false;
      this.$emit('handleClose');
      return;
    }
    if (this.currentIndex >= this.noticeList.length) {
      this.currentIndex = this.noticeList.length - 1;
    }
    this.resetForm();
  }

  public handleBeforeClose(done: () => void) {
    if (!this.canClose) {
      this.$message.warning(this.$t('popupNotice.forceShowCloseBlocked').toString());
      return;
    }
    this.handleClose().then(() => done());
  }

  public async handleClose() {
    const notice = this.currentNotice;
    if (!notice) return;
    if (!this.canClose) {
      this.$message.warning(this.$t('popupNotice.forceShowCloseBlocked').toString());
      return;
    }
    this.loading = true;
    try {
      await popupNoticeCloseApi(notice.id);
      this.finishCurrent();
    } finally {
      this.loading = false;
    }
  }

  public async handleAck() {
    const notice = this.currentNotice;
    if (!notice) return;
    this.loading = true;
    try {
      await popupNoticeAckApi(notice.id);
      this.finishCurrent();
    } finally {
      this.loading = false;
    }
  }

  public async handleDelay() {
    const notice = this.currentNotice;
    if (!notice || !this.form.delayToTime) {
      this.$message.warning(this.$t('popupNotice.selectDelayTime').toString());
      return;
    }
    this.loading = true;
    try {
      await popupNoticeSubmitFeedbackApi({
        fkPopupNoticeTaskId: notice.id,
        feedbackType: 'DELAY',
        applyDelayToTime: this.form.delayToTime
      });
      this.$message.success(this.$t('popupNotice.submitSuccess').toString());
      this.finishCurrent();
    } finally {
      this.loading = false;
    }
  }

  public async handleFeedback() {
    const notice = this.currentNotice;
    const feedbackText = `${this.form.feedbackText || ''}`.trim();
    if (!notice || !feedbackText) {
      this.$message.warning(this.$t('popupNotice.feedbackRequired').toString());
      return;
    }
    this.loading = true;
    try {
      await popupNoticeSubmitFeedbackApi({
        fkPopupNoticeTaskId: notice.id,
        feedbackType: 'TEXT',
        feedbackText
      });
      this.$message.success(this.$t('popupNotice.submitSuccess').toString());
      this.finishCurrent();
    } finally {
      this.loading = false;
    }
  }

  public async handleSurvey() {
    const notice = this.currentNotice;
    if (!notice) return;
    const missingRequired = this.questionList.some(question => {
      if (question.isRequired !== 1) return false;
      const answer = this.form.answerMap[question.id];
      return answer === undefined || answer === null || String(answer).trim() === '';
    });
    if (missingRequired) {
      this.$message.warning(this.$t('popupNotice.answerRequired').toString());
      return;
    }
    const answerList = this.questionList.map(question => {
      const answer = this.form.answerMap[question.id];
      if (question.questionType === 'TEXT') {
        return {
          fkPopupNoticeQuestionId: question.id,
          answerText: typeof answer === 'string' ? answer.trim() : ''
        };
      }
      return {
        fkPopupNoticeQuestionId: question.id,
        fkPopupNoticeQuestionOptionId: answer,
        answerValue: answer ? String(answer) : ''
      };
    });
    this.loading = true;
    try {
      await popupNoticeSubmitFeedbackApi({
        fkPopupNoticeTaskId: notice.id,
        feedbackType: 'SURVEY',
        answerList
      });
      this.$message.success(this.$t('popupNotice.submitSuccess').toString());
      this.finishCurrent();
    } finally {
      this.loading = false;
    }
  }

  public getPriorityLabel(priorityLevel?: number) {
    const level = Number(priorityLevel || 0);
    if (level >= 90) return this.$t('popupNotice.criticalNotice');
    if (level >= 60) return this.$t('popupNotice.importantNotice');
    return this.$t('popupNotice.normalNotice');
  }

  public getPriorityClass(priorityLevel?: number) {
    const level = Number(priorityLevel || 0);
    if (level >= 90) return 'critical';
    if (level >= 60) return 'important';
    return 'normal';
  }

  public getNoticeTypeLabel(noticeType?: string) {
    if (!noticeType) return '--';
    const labelMap: any = {
      ANNOUNCEMENT: this.$t('popupNotice.noticeTypeAnnouncement'),
      DATA_OVERWRITE: this.$t('popupNotice.noticeTypeDataOverwrite'),
      UPGRADE: this.$t('popupNotice.noticeTypeUpgrade'),
      VOTE: this.$t('popupNotice.noticeTypeVote'),
      SURVEY: this.$t('popupNotice.noticeTypeSurvey'),
      RESEARCH: this.$t('popupNotice.noticeTypeResearch')
    };
    return labelMap[noticeType] || noticeType;
  }

  private escapeHtml(content: string) {
    return content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private decodeHtmlEntities(content: string) {
    if (!content || typeof document === 'undefined') return content;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = content;
    return textarea.value;
  }

  private formatPopupNoticeContent(content: string) {
    const normalizedContent = `${content || ''}`.trim();
    if (!normalizedContent) return '<p>--</p>';
    if (/<[a-z][\s\S]*>/i.test(normalizedContent)) return normalizedContent;
    const decodedContent = this.decodeHtmlEntities(normalizedContent);
    return `<p>${this.escapeHtml(decodedContent).replace(/\r?\n/g, '<br>')}</p>`;
  }
}
